import React, { type FC } from 'react';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import NavLink from './nav-link';
import Heading from '../heading/heading';

type NavDropdownMenuGroupProps = {
  link: Lucid.Directus.Generated.Nav_Section_Translations_Dropdown_Section;
  isGroupSplit?: boolean;
  index: number;
  isGlobalNav?: boolean;
  isDropdownOpen?: boolean;
  className?: string;
};

const renderDropdownLink = ({
  link,
  index,
  isGlobalNav,
  isDropdownOpen,
}: {
  link: Lucid.Directus.Generated.Dropdown_Section_Dropdown_Link;
  index: number;
  isGlobalNav?: boolean;
  isDropdownOpen?: boolean;
}) => (
  <li
    key={`navDropdownMenuGroupLink-${link.dropdown_link_id?.id}-${index + 1}`}
    css={theme => [
      {
        label: 'navDropdownMenuGroupLinkContainer',
        display: 'flex',
        alignItems: 'center',
        flex: '0 1 100%',
        maxWidth: '100%',
      },
      isGlobalNav && {
        '& > a': {
          '&:active, &:hover, &:focus, &:focus-visible': {
            color: theme.colors.blue[70],
          },
          '&:focus, &:focus-visible': {
            outlineColor: theme.colors.blue[70],
          },
        },
      },
    ]}
    tabIndex={-1}
  >
    <NavLink
      isInDropdown
      isGlobalNav={isGlobalNav}
      link={link.dropdown_link_id?.link}
      linkImage={link.dropdown_link_id?.image}
      linkSubtext={link.dropdown_link_id?.text}
      shouldRemoveFromTabIndex={!isDropdownOpen}
      variant="subNavLink"
    />
  </li>
);

const NavDropdownMenuGroup: FC<NavDropdownMenuGroupProps> = ({
  link,
  isGroupSplit,
  index,
  isGlobalNav,
  isDropdownOpen,
  className,
}) => (
  <Box
    css={theme => [
      {
        label: 'navDropdownMenuGroup',
        justifyContent: 'space-between',
        flex: '0 1 100%',
        width: 'calc(100% + 24px)',
        minWidth: '210px',
        maxWidth: '100%',
        '& > ul:last-of-type': {
          paddingBottom: theme.space.s40,
        },
        [theme.breakpoints.bp1024.min]: {
          flex: '0 1 100%',
          width: '210px',
          maxWidth: '210px',
        },
      },
      isGroupSplit && {
        [theme.breakpoints.bp1024.min]: {
          marginTop: theme.space.s24,
        },
      },
    ]}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
    data-testid="navDropdownMenuGroup"
  >
    {link.dropdown_section_id?.label && (
      <Heading
        aria-level={3}
        as="h3"
        dataElementPageLocation={isGlobalNav ? 'globalNav' : 'siteNav'}
        id={`navDropdownMenuGroupLabel-${link.dropdown_section_id.id}-${index + 1}`}
        variant="headingM"
      >
        {link.dropdown_section_id.label}
        <hr />
      </Heading>
    )}
    <ul
      aria-labelledby={`navDropdownMenuGroupLabel-${link.dropdown_section_id?.id}-${index + 1}`}
      css={{
        label: 'navDropdownMenuGroupUL',
        margin: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {link.dropdown_section_id?.link_set?.map(link_set =>
        renderDropdownLink({
          link: link_set as Lucid.Directus.Generated.Dropdown_Section_Dropdown_Link,
          index,
          isGlobalNav,
          isDropdownOpen,
        }),
      )}
    </ul>
  </Box>
);

export { NavDropdownMenuGroup };
export default NavDropdownMenuGroup;
